import { useEffect, useState } from "react";
import type { CSSProperties } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useSession } from "../lib/session";
import { Button } from "./ui";
import { initials } from "../lib/format";
import { useTodoCounts } from "../features/todo/useTodo";

type ModuleKey =
  | "todo"
  | "vehicles"
  | "sales"
  | "projects"
  | "service"
  | "finance"
  | "stock"
  | "people"
  | "admin"
  | "products"
  | "hs"
  | "kpi";

/**
 * Scopes --accent/--accent-soft to one module's colour for everything
 * inside the wrapping div — the section dot (.nav-section::before) and
 * the active-item indicator (.nav-item[aria-current="page"]) both read
 * --accent already, so overriding it here is enough to colour-code each
 * nav group without a bespoke CSS class per module.
 */
function sectionAccent(mod: ModuleKey): CSSProperties {
  return { "--accent": `var(--mod-${mod})`, "--accent-soft": `var(--mod-${mod}-soft)` } as CSSProperties;
}

function moduleTitle(pathname: string): string {
  if (pathname === "/") return "Home";
  if (pathname.startsWith("/todo")) return "To Do";
  if (pathname.startsWith("/sales")) return "Sales";
  if (pathname.startsWith("/projects")) return "Projects";
  if (pathname.startsWith("/leads")) return "Leads";
  if (pathname.startsWith("/quotes")) return "Quotes";
  if (pathname.startsWith("/people")) return "People";
  if (pathname.startsWith("/customers")) return "Customers";
  if (pathname.startsWith("/service")) return "Service";
  if (pathname.startsWith("/wip")) return "Projects";
  if (pathname.startsWith("/commissioning")) return "Commissioning";
  if (pathname.startsWith("/finance")) return "Finance";
  if (pathname.startsWith("/stock")) return "Stock";
  if (pathname.startsWith("/products")) return "Product Library";
  if (pathname.startsWith("/hs")) return "Health & Safety";
  if (pathname.startsWith("/admin") || pathname.startsWith("/platform")) return "Platform";
  if (pathname.startsWith("/kpi") || pathname.startsWith("/display")) return "Dashboards";
  return "Vehicle Management";
}

/**
 * Which nav group a route belongs to, for the collapsible sidebar below.
 * Null means no group owns this route (Home, the static WIP placeholder) —
 * every group starts collapsed there. Mirrors moduleTitle's own
 * prefix-matching rather than introducing a second routing concept.
 */
function activeSection(pathname: string): ModuleKey | null {
  if (pathname.startsWith("/todo")) return "todo";
  if (pathname.startsWith("/vehicles")) return "vehicles";
  /* Sales and Projects are groups, so every route belonging to a member
     module keeps its group open -- being deep in a quote should not
     collapse the group you walked in through. */
  if (
    pathname.startsWith("/sales") ||
    pathname.startsWith("/leads") ||
    pathname.startsWith("/quotes") ||
    pathname.startsWith("/customers")
  )
    return "sales";
  if (pathname.startsWith("/projects") || pathname.startsWith("/wip") || pathname.startsWith("/commissioning"))
    return "projects";
  if (pathname.startsWith("/service")) return "service";
  if (pathname.startsWith("/finance")) return "finance";
  if (pathname.startsWith("/stock")) return "stock";
  if (pathname.startsWith("/people")) return "people";
  if (pathname.startsWith("/products")) return "products";
  if (pathname.startsWith("/hs")) return "hs";
  if (pathname.startsWith("/admin") || pathname.startsWith("/platform")) return "admin";
  if (pathname.startsWith("/kpi")) return "kpi";
  return null;
}

/**
 * Home ("/") is the app's back-navigation floor: pressing back (or, on an
 * installed PWA, swiping from the screen edge) from there must not be
 * able to walk back into the OAuth redirect chain — that history is
 * real, cross-origin browser history the History API cannot delete, only
 * outrun. On every back step that lands on "/", this plants one more
 * same-URL entry on top of it, so the *next* back step lands on our own
 * entry again instead of escaping further.
 *
 * Exactly two pushes total per need, not one per "arrival": a single
 * one-time push on mount (AppShell mounts once per session, not once
 * per navigation — it's the layout route, only its <Outlet/> content
 * swaps) protects the very first swipe of the session, before any
 * popstate has fired yet; the popstate listener protects every
 * subsequent one. An earlier version instead pushed from a
 * pathname-keyed effect, which re-fires on every "/" arrival including
 * ones popstate itself just caused — so every single trip through
 * Home was silently stacking two duplicate history entries instead of
 * one. Confirmed as the cause of "every second screen white": with
 * entries piling up in pairs, alternate ones plausibly never got a
 * settled WebKit snapshot captured for them (nothing about a same-URL
 * pushState causes a repaint to snapshot), so every other back-swipe
 * hit an entry needing a fresh reconstruction instead of a live resume.
 */
function useHistoryFloor() {
  useEffect(() => {
    if (window.location.pathname === "/") window.history.pushState(null, "", "/");
  }, []);

  useEffect(() => {
    function onPopState() {
      if (window.location.pathname !== "/") return;
      // Deferred to the next frame: popstate fires the instant a
      // swipe-back gesture completes, while iOS's own transition
      // animation for it is very likely still settling. Re-pushing state
      // synchronously in the same tick fights that in-progress animation
      // — a plausible cause of a hang-on-white, not just the flash the
      // rest of this trap was built for.
      requestAnimationFrame(() => {
        if (window.location.pathname === "/") window.history.pushState(null, "", "/");
      });
    }
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);
}

/**
 * Navigation is filtered by permission purely so people are not shown doors
 * they cannot open. Hiding a link is not a security control — every route
 * behind it is protected by RLS at the database.
 */
export default function AppShell() {
  const { profile, can, signOut } = useSession();
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);
  const location = useLocation();
  const navigate = useNavigate();
  useHistoryFloor();
  // Every group starts collapsed (Home -> activeSection is null). Entering
  // a section auto-expands it and, by replacing rather than merging this
  // state, collapses whatever else was open — moving Service -> Quotes
  // collapses Service the same tick Quotes expands. A manual header click
  // (toggleSection, below) is untouched by this effect until the *next*
  // real navigation, so a user can peek another group open without losing
  // their place on the current page.
  const [openSection, setOpenSection] = useState<ModuleKey | null>(() => activeSection(location.pathname));
  useEffect(() => {
    setOpenSection(activeSection(location.pathname));
  }, [location.pathname]);

  function toggleSection(key: ModuleKey) {
    setOpenSection((cur) => (cur === key ? null : key));
  }

  // Shares its four queries (by key) with the To Do page itself, so the
  // badge and the list can never disagree and the shell costs no extra
  // round trips. Undefined while loading -- see the Home tile.
  const todo = useTodoCounts();
  const todoBadge = todo.isLoading || todo.total === 0 ? null : todo.total;

  return (
    <div className="shell">
      {open && (
        <div
          className="backdrop"
          style={{ zIndex: 65 }}
          onClick={close}
          aria-hidden="true"
        />
      )}

      {/* Mobile-only stand-in for the OS edge-swipe-back gesture — same
          one-step-back action, just also reachable as a tap for anyone who
          doesn't know/use the swipe, or whose swipe the sidebar drawer eats.
          Hidden on Home (nothing to go back to) and while the drawer is
          open (the backdrop already sits above it). */}
      {!open && location.pathname !== "/" && (
        <button
          type="button"
          className="back-nav"
          onClick={() => navigate(-1)}
          aria-label="Back"
        >
          ←
        </button>
      )}

      <nav className={`sidebar${open ? " open" : ""}`} aria-label="Main">
        <Link to="/" className="brand" onClick={close}>
          <img className="brand-mark" src="/icon-48.png" alt="" aria-hidden="true" />
          Viewpoint Core
        </Link>

        <NavLink to="/" end className="nav-item" onClick={close}>
          Home
        </NavLink>

        {/* A flat item, not a collapsible group: To Do has nothing under
            it, and the count is the whole point of it being in the nav at
            all -- it has to be readable without expanding anything. */}
        <div style={sectionAccent("todo")}>
          <NavLink to="/todo" className="nav-item nav-item-count" onClick={close}>
            <span>To Do</span>
            {todoBadge != null && (
              <span className="nav-count" aria-label={`${todoBadge} outstanding`}>
                {todoBadge > 99 ? "99+" : todoBadge}
              </span>
            )}
          </NavLink>
        </div>

        <div style={sectionAccent("vehicles")}>
          <button
            type="button"
            className="nav-section nav-section-toggle"
            aria-expanded={openSection === "vehicles"}
            onClick={() => toggleSection("vehicles")}
          >
            <span>Vehicles</span>
            <span className="nav-chevron" aria-hidden="true">{openSection === "vehicles" ? "▾" : "▸"}</span>
          </button>
          {openSection === "vehicles" && (
            <>
              <NavLink to="/vehicles/fleet" className="nav-item" onClick={close}>
                Fleet
              </NavLink>
              <div className="nav-divider" role="separator" aria-hidden="true" />
              <NavLink to="/vehicles/list" className="nav-item" onClick={close}>
                All vehicles
              </NavLink>
              <NavLink to="/vehicles/accident" className="nav-item" onClick={close}>
                In case of accident
              </NavLink>
              <NavLink to="/vehicles/issues" className="nav-item" onClick={close}>
                Issues
              </NavLink>
              <NavLink to="/vehicles/pool-booking" className="nav-item" onClick={close}>
                Pool booking
              </NavLink>
              <NavLink to="/vehicles/report-issue" className="nav-item" onClick={close}>
                Report an issue
              </NavLink>
            </>
          )}
        </div>

        {/* Sales: Customers, Leads and Quotes under one heading. Each keeps
            its own landing page, so Quotes still opens on Pipeline/All
            quotes/New quote rather than having those flattened up here --
            the grouping changes where you enter, not how a module works
            once you are in it. */}
        {(can("can_view_service") || can("can_view_quotes") || can("can_view_projects")) && (
          <div style={sectionAccent("sales")}>
            <button
              type="button"
              className="nav-section nav-section-toggle"
              aria-expanded={openSection === "sales"}
              onClick={() => toggleSection("sales")}
            >
              <span>Sales</span>
              <span className="nav-chevron" aria-hidden="true">{openSection === "sales" ? "▾" : "▸"}</span>
            </button>
            {openSection === "sales" && (
              <>
                <NavLink to="/customers" end className="nav-item" onClick={close}>
                  Customers
                </NavLink>
                {can("can_view_quotes") && (
                  <>
                    <div className="nav-divider" role="separator" aria-hidden="true" />
                    <NavLink to="/leads" end className="nav-item" onClick={close}>
                      Leads
                    </NavLink>
                    <NavLink to="/quotes" end className="nav-item" onClick={close}>
                      Quotes
                    </NavLink>
                  </>
                )}
              </>
            )}
          </div>
        )}

        {/* Projects: the project itself and its commissioning, which are
            two halves of the same job and were two top-level sections. */}
        {can("can_view_wip") && (
          <div style={sectionAccent("projects")}>
            <button
              type="button"
              className="nav-section nav-section-toggle"
              aria-expanded={openSection === "projects"}
              onClick={() => toggleSection("projects")}
            >
              <span>Projects</span>
              <span className="nav-chevron" aria-hidden="true">{openSection === "projects" ? "▾" : "▸"}</span>
            </button>
            {openSection === "projects" && (
              <>
                <NavLink to="/wip/jobs" className="nav-item" onClick={close}>
                  Jobs
                </NavLink>
                <div className="nav-divider" role="separator" aria-hidden="true" />
                <NavLink to="/commissioning" end className="nav-item" onClick={close}>
                  Commissioning
                </NavLink>
                <NavLink to="/wip/my-schedule" className="nav-item" onClick={close}>
                  My Schedule
                </NavLink>
                <NavLink to="/wip/planner" className="nav-item" onClick={close}>
                  Planner
                </NavLink>
                <NavLink to="/wip/analysis" className="nav-item" onClick={close}>
                  Project Analysis
                </NavLink>
              </>
            )}
          </div>
        )}

        {can("can_view_service") && (
          <div style={sectionAccent("service")}>
            <button
              type="button"
              className="nav-section nav-section-toggle"
              aria-expanded={openSection === "service"}
              onClick={() => toggleSection("service")}
            >
              <span>Service</span>
              <span className="nav-chevron" aria-hidden="true">{openSection === "service" ? "▾" : "▸"}</span>
            </button>
            {openSection === "service" && (
              <>
                {/* Pinned high-priority first (Contracts, Service Visits),
                    then a divider, then everything else A–Z. Client Offices
                    stays hidden — route /service/sites and its data remain. */}
                <NavLink to="/service/contracts" className="nav-item" onClick={close}>
                  Contracts
                </NavLink>
                <NavLink to="/service/visits" className="nav-item" onClick={close}>
                  Service Visits
                </NavLink>
                <div className="nav-divider" role="separator" aria-hidden="true" />
                <NavLink to="/service/activity-report" className="nav-item" onClick={close}>
                  Client Report
                </NavLink>
                <NavLink to="/service/dashboard" className="nav-item" onClick={close}>
                  Dashboard
                </NavLink>
                <NavLink to="/service/issues" className="nav-item" onClick={close}>
                  Outstanding Service Issues
                </NavLink>
                <NavLink to="/service/phone-log" className="nav-item" onClick={close}>
                  Phone Log
                </NavLink>
                <NavLink to="/service/recurring-services" className="nav-item" onClick={close}>
                  Recurring Services
                </NavLink>
                <NavLink to="/service/renewals" className="nav-item" onClick={close}>
                  Renewals
                </NavLink>
                <NavLink to="/service/tickets" className="nav-item" onClick={close}>
                  Tickets
                </NavLink>
              </>
            )}
          </div>
        )}

        {can("can_view_finance") && (
          <div style={sectionAccent("finance")}>
            <button
              type="button"
              className="nav-section nav-section-toggle"
              aria-expanded={openSection === "finance"}
              onClick={() => toggleSection("finance")}
            >
              <span>Finance</span>
              <span className="nav-chevron" aria-hidden="true">{openSection === "finance" ? "▾" : "▸"}</span>
            </button>
            {openSection === "finance" && (
              <>
                <NavLink to="/finance/ledger" className="nav-item" onClick={close}>
                  Ledger
                </NavLink>
                {can("can_manage_finance") && (
                  <>
                    <div className="nav-divider" role="separator" aria-hidden="true" />
                    <NavLink to="/finance/import" className="nav-item" onClick={close}>
                      Import
                    </NavLink>
                  </>
                )}
              </>
            )}
          </div>
        )}

        {can("can_view_stock") && (
          <div style={sectionAccent("stock")}>
            <button
              type="button"
              className="nav-section nav-section-toggle"
              aria-expanded={openSection === "stock"}
              onClick={() => toggleSection("stock")}
            >
              <span>Stock</span>
              <span className="nav-chevron" aria-hidden="true">{openSection === "stock" ? "▾" : "▸"}</span>
            </button>
            {openSection === "stock" && (
              <>
                <NavLink to="/stock/dashboard" className="nav-item" onClick={close}>
                  Dashboard
                </NavLink>
                <div className="nav-divider" role="separator" aria-hidden="true" />
                <NavLink to="/stock/items" className="nav-item" onClick={close}>
                  Items
                </NavLink>
                <NavLink to="/stock/loans" className="nav-item" onClick={close}>
                  Loans
                </NavLink>
                <NavLink to="/stock/actions" className="nav-item" onClick={close}>
                  Movement Actions
                </NavLink>
              </>
            )}
          </div>
        )}

        {(can("can_view_quotes") || can("can_view_service")) && (
          <div style={sectionAccent("products")}>
            <button
              type="button"
              className="nav-section nav-section-toggle"
              aria-expanded={openSection === "products"}
              onClick={() => toggleSection("products")}
            >
              <span>Product Library</span>
              <span className="nav-chevron" aria-hidden="true">{openSection === "products" ? "▾" : "▸"}</span>
            </button>
            {openSection === "products" && (
              <>
                <NavLink to="/products" end className="nav-item" onClick={close}>
                  Product Library
                </NavLink>
                <div className="nav-divider" role="separator" aria-hidden="true" />
                <NavLink to="/products/equipment-models" className="nav-item" onClick={close}>
                  Equipment Models
                </NavLink>
              </>
            )}
          </div>
        )}

        <div style={sectionAccent("people")}>
          <button
            type="button"
            className="nav-section nav-section-toggle"
            aria-expanded={openSection === "people"}
            onClick={() => toggleSection("people")}
          >
            <span>People</span>
            <span className="nav-chevron" aria-hidden="true">{openSection === "people" ? "▾" : "▸"}</span>
          </button>
          {openSection === "people" && (
            <>
              {can("can_manage_people") && (
                <>
                  <NavLink to="/people/list" className="nav-item" onClick={close}>
                    People
                  </NavLink>
                  <div className="nav-divider" role="separator" aria-hidden="true" />
                </>
              )}
              {can("can_view_compliance") && (
                <NavLink to="/people/compliance" className="nav-item" onClick={close}>
                  Compliance
                </NavLink>
              )}
              <NavLink to="/people/insurance" className="nav-item" onClick={close}>
                Insurance
              </NavLink>
            </>
          )}
        </div>

        {/* Health & Safety nav entry hidden on request -- the feature, its
            routes and its data are untouched underneath; this is purely a
            "false &&" so it's a one-line revert to bring back. */}
        {false && can("can_view_hs") && (
          <div style={sectionAccent("hs")}>
            <button
              type="button"
              className="nav-section nav-section-toggle"
              aria-expanded={openSection === "hs"}
              onClick={() => toggleSection("hs")}
            >
              <span>Health &amp; Safety</span>
              <span className="nav-chevron" aria-hidden="true">{openSection === "hs" ? "▾" : "▸"}</span>
            </button>
            {openSection === "hs" && (
              <>
                <NavLink to="/hs/weekly" className="nav-item" onClick={close}>
                  Weekly checks
                </NavLink>
                <div className="nav-divider" role="separator" aria-hidden="true" />
                <NavLink to="/hs/annual" className="nav-item" onClick={close}>
                  Annual services
                </NavLink>
                {can("can_manage_hs") && (
                  <NavLink to="/hs/settings" className="nav-item" onClick={close}>
                    Check settings
                  </NavLink>
                )}
                <NavLink to="/hs/report" className="nav-item" onClick={close}>
                  Compliance report
                </NavLink>
                {can("can_manage_hs") && (
                  <NavLink to="/hs/weekly/new" className="nav-item" onClick={close}>
                    Record a check
                  </NavLink>
                )}
              </>
            )}
          </div>
        )}

        {can("can_view_platform") && (
          <div style={sectionAccent("admin")}>
            <button
              type="button"
              className="nav-section nav-section-toggle"
              aria-expanded={openSection === "admin"}
              onClick={() => toggleSection("admin")}
            >
              <span>Platform</span>
              <span className="nav-chevron" aria-hidden="true">{openSection === "admin" ? "▾" : "▸"}</span>
            </button>
            {openSection === "admin" && (
              <>
                {can("can_view_supabase_usage") && (
                  <>
                    <NavLink to="/admin/supabase-usage" className="nav-item" onClick={close}>
                      Billing & usage
                    </NavLink>
                    <div className="nav-divider" role="separator" aria-hidden="true" />
                  </>
                )}
                {can("can_view_ai_usage") && (
                  <NavLink to="/admin/claude-usage" className="nav-item" onClick={close}>
                    Claude API usage
                  </NavLink>
                )}
                {can("can_manage_integrations") && (
                  <NavLink to="/platform/integrations" className="nav-item" onClick={close}>
                    Integrations
                  </NavLink>
                )}
                <NavLink to="/platform/support-ticketing" className="nav-item" onClick={close}>
                  Support ticketing
                </NavLink>
                <NavLink to="/platform/system-health" className="nav-item" onClick={close}>
                  System health
                </NavLink>
              </>
            )}
          </div>
        )}

        {can("can_view_kpi_dashboards") && (
          <div style={sectionAccent("kpi")}>
            <button
              type="button"
              className="nav-section nav-section-toggle"
              aria-expanded={openSection === "kpi"}
              onClick={() => toggleSection("kpi")}
            >
              <span>Dashboards</span>
              <span className="nav-chevron" aria-hidden="true">{openSection === "kpi" ? "▾" : "▸"}</span>
            </button>
            {openSection === "kpi" && (
              <>
                <NavLink to="/kpi" end className="nav-item" onClick={close}>
                  Dashboards
                </NavLink>
                <div className="nav-divider" role="separator" aria-hidden="true" />
                {can("can_manage_kpi_dashboards") && (
                  <>
                    <NavLink to="/kpi/display-keys" className="nav-item" onClick={close}>
                      Display Keys
                    </NavLink>
                    <NavLink to="/kpi/trusted-networks" className="nav-item" onClick={close}>
                      Trusted Networks
                    </NavLink>
                  </>
                )}
                <NavLink to="/kpi/views" className="nav-item" onClick={close}>
                  View Library
                </NavLink>
              </>
            )}
          </div>
        )}

        <div className="spacer" />
        <div className="nav-section">Signed in</div>
        <div style={{ padding: "0 10px 10px" }}>
          <div className="small" style={{ fontWeight: 600 }}>
            {profile?.full_name ?? "—"}
          </div>
          <div className="muted small" style={{ marginBottom: 8 }}>
            {profile?.email}
          </div>
          <div className="muted small" style={{ marginBottom: 8 }}>
            {can("can_manage_vehicles") ? "Can administer vehicles" : "Read-only fleet access"}
            {can("can_view_vehicle_telemetry") ? " · tracking" : ""}
          </div>
          <Button size="sm" block onClick={() => void signOut()}>
            Sign out
          </Button>
        </div>
      </nav>

      <div className="main">
        <header className="topbar">
          <Button
            className="mobile-only"
            variant="ghost"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle navigation"
            aria-expanded={open}
          >
            ☰
          </Button>
          <strong style={{ letterSpacing: "-0.01em" }}>{moduleTitle(location.pathname)}</strong>
          <span className="spacer" />
          <span
            className="badge"
            title={profile?.email ?? ""}
            aria-label={`Signed in as ${profile?.full_name ?? "unknown"}`}
          >
            {initials(profile?.full_name)}
          </span>
        </header>

        <main className="content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
